import { QueryMode } from "../../../gql/hooks";
import { LastVisitOptions, PosFilters } from "./types";
import getFirstDayOfLastYear from "./utils/getFirstDayOfLastYear";
import { getISOStringDate } from "../../../utils/getISOStringDate";
import {
  MAX_VOLUME_RANGE,
  MIN_VOLUME_RANGE,
} from "../../../pages/PosList/partials/posfilters/partials/FilterVolumeRange/volumeRange";

export const LIMIT = 100;

/**
 * The query variables a set of filters describes.
 *
 * Pulled out of useFilterVariables so the slice can seed `queryVariables` with
 * it. The two used to be written by hand and drift apart: the slice seeded
 * `{ take, skip, isOwner, userLocation }` while the hook produced the same plus
 * `beerTAM: {}`, the empty filter the default volume range yields. Apollo keys a
 * query on its variables, and an empty object is not an absent key -- so the
 * first render queried with the seed, the hook's mount effect replaced it, and
 * every page load paid for two identical result sets. Deriving both from here
 * makes them equal by construction, and Apollo stops at one round trip.
 */
export default function buildQueryVariables(filters: PosFilters): any {
  let filtersObj: any = {
    take: LIMIT,
    skip: 0,
    isOwner: filters.isOwner,
  };

  // Missing products, if found, call us
  if (filters.missingProducts.length) {
    const date = getFirstDayOfLastYear();

    const formattedMissingProducts = filters.missingProducts.reduce(
      (acc, { id, type }) => {
        const index = acc.findIndex((_: any) => _.container === type);
        if (index >= 0 && acc[index]) {
          acc[index].productIdNew.in.push(id);
        } else {
          acc.push({
            productIdNew: { in: [id] },
            container: type,
            lastSaleDate: { gt: date.toISOString() },
          });
        }

        return acc;
      },
      [] as any,
    );
    if (!filtersObj.soldProducts) filtersObj.soldProducts = {};
    filtersObj.soldProducts.none = { OR: formattedMissingProducts };
  }

  // Existing products, therefore not missing, so don't call us
  if (filters.existingProducts.length) {
    const date = getFirstDayOfLastYear();

    const formattedExistingProducts = filters.existingProducts.map(
      ({ id, type }) => ({
        soldProducts: {
          some: {
            productId: id,
            container: type,
            lastSaleDate: { gt: date.toISOString() },
          },
        },
      }),
    );
    if (!filtersObj.and) filtersObj.and = [];
    filtersObj.and = filtersObj.and.concat(formattedExistingProducts);
  }

  // Search, maybe searching the missing products, idk
  if (filters.search.length >= 3) {
    filtersObj.search = {
      contains: filters.search.toUpperCase(),
      mode: QueryMode.Insensitive,
    };
  }

  // Segments
  if (filters.segmentTypes.length) {
    filtersObj.segmentType = { in: filters.segmentTypes };
  }

  if (filters.segments.length) {
    filtersObj.segment = { in: filters.segments };
  }

  // Brands
  if (filters.brandIds.length) {
    filtersObj.brandId = { in: filters.brandIds };
  }

  // Visits, you should visit me more often
  switch (filters.lastVisitDate) {
    case LastVisitOptions.LAST_30_DAYS:
      filtersObj.lastVisitDate = {
        lte: getISOStringDate(new Date(), { addDays: -30 }),
      };
      break;
    case LastVisitOptions.LAST_60_DAYS:
      filtersObj.lastVisitDate = {
        lte: getISOStringDate(new Date(), { addDays: -60 }),
      };
      break;
    case LastVisitOptions.LAST_90_DAYS:
      filtersObj.lastVisitDate = {
        lte: getISOStringDate(new Date(), { addDays: -90 }),
      };
      break;
    case LastVisitOptions.LAST_180_DAYS:
      filtersObj.lastVisitDate = {
        lte: getISOStringDate(new Date(), { addDays: -180 }),
      };
      break;
    case LastVisitOptions.LAST_YEAR:
      filtersObj.lastVisitDate = {
        lte: getISOStringDate(new Date(), { addYears: -1 }),
      };
      break;
    case LastVisitOptions.CURRENT_YEAR:
      filtersObj.lastVisitDate = {
        lte: new Date(new Date().getFullYear(), 0, 1).toISOString(),
      };
      break;
    default:
      break;
  }

  // Partners (not in crime tho)
  filtersObj.laLiga = filters.laLiga;
  filtersObj.rentabilibar = filters.rentabilibar;

  // Status
  filtersObj.isActive = filters.isActive;
  filtersObj.underContract = filters.underContract;

  // Volume
  if (filters.volumeRange) {
    filtersObj.beerTAM = {};

    let lowerRange = filters.volumeRange[0];
    filtersObj.beerTAM.gte =
      lowerRange <= MIN_VOLUME_RANGE ? undefined : lowerRange;

    let upperRange = filters.volumeRange[1];
    filtersObj.beerTAM.lte =
      upperRange >= MAX_VOLUME_RANGE ? undefined : upperRange;
  }

  // Postal Codes
  if (filters.postalCodes?.length) {
    filtersObj.postalCodes = filters.postalCodes;
  }

  return filtersObj;
}
